import React, { ReactNode, useState } from 'react';
import extendClassNameWithModifier from '../../../lib/bemUtils/bem';
import { ContentBlockHeadline } from '../../../_lib/components/ContentBlockHeadline';
import layoutVariations from '../../blocks/LayoutGroup/layoutVariations';
import ModalDialog from '../ModalDialog/ModalDialog';
import { ModalDialogModifier } from '../ModalDialog/ModalDialog.modifier';
import { Figure } from './partials/Figure';
import { useMediaInformation } from './useMediaInformation';
import { useSonTranslation } from '../../../translation/useSonTranslation';

export type Size = 'large' | 'medium' | 'small' | undefined;

export type Media = {
  copyright?: string;
  caption?: string;
  alt?: string;
  size?: Size;
  imageType?: string;
  title?: string;
  contentType: 'image' | 'iframe';
};

type MediaInformationProps = {
  media: Media;
  children: ReactNode;
  groupLayout?: keyof typeof layoutVariations;
  hasPopUp?: boolean;
  popUpContent?: ReactNode;
  isPopUpImage?: boolean;
  slider?: boolean;
  onClickForPopUp?: () => void;
  isImageInPopUpSlider?: boolean;
};

function getProperty<T, K extends keyof T>(obj: T, key: K): T[K] {
  return obj[key];
}

export function MediaInformation({
  media,
  children,
  groupLayout,
  hasPopUp,
  popUpContent,
  isPopUpImage,
  slider,
  onClickForPopUp,
  isImageInPopUpSlider,
}: MediaInformationProps) {
  const [popUpActive, setPopUpActive] = useState(false);
  const { t } = useSonTranslation('MediaInformation');
  const { getModifier, hasPadding } = useMediaInformation();

  /**
   * No caption in iframe pop-up
   * */
  const getCaption = (isPopUp: boolean, caption?: string): string | undefined => {
    if (media.contentType === 'image' || (media.contentType === 'iframe' && !isPopUp)) {
      return caption;
    }
    return undefined;
  };

  const getContent = (isPopUp: boolean): ReactNode => {
    return (
      <Figure
        isPopUp={isPopUp}
        hasPopUp={hasPopUp}
        isPopUpImage={isPopUpImage}
        popUpContent={popUpContent}
        onClick={onClickForPopUp ? () => onClickForPopUp() : () => setPopUpActive(true)}
        padding={hasPadding(media)}
        copyright={media.copyright}
        caption={getCaption(isPopUp, media.caption)}
        title={media.title}
        alt={media.alt}
      >
        {children}
      </Figure>
    );
  };

  return (
    <>
      <div
        className={extendClassNameWithModifier(
          'media-information',
          getModifier(
            media,
            groupLayout ? getProperty(layoutVariations, groupLayout as any) : '',
            hasPopUp && popUpActive,
            slider,
            isImageInPopUpSlider,
          ),
        )}
      >
        {media.title && <ContentBlockHeadline title={media.title} />}
        {getContent(false)}
      </div>
      {hasPopUp && popUpActive && (
        <ModalDialog
          closeModal={() => setPopUpActive(false)}
          headline={t(`modalDialogTitle.${media?.contentType}`)}
          modifier={ModalDialogModifier.POP_UP}
        >
          {getContent(true)}
        </ModalDialog>
      )}
    </>
  );
}
